// Auto-generated reminders for a deal (Sections 3.4, 3.5, 5.4). Each has a stable
// sourceKey, so regenerating after a date change moves the reminder instead of
// creating a duplicate.

import { addMonths, addYears, maxDate, type ISODate } from '../dates';
import { effectivePaymentStatus, leaseExpiration, underContractDates, type DealDates, type PaymentStatus } from './payments';
import { isLeaseDealType, type DealType } from './stages';

export type ReminderType =
  | 'manual_follow_up'
  | 'deal_next_step'
  | 'post_close_touchpoint'
  | 'payment_chase'
  | 'lease_expiration_touchpoint';

export type ReminderStatus = 'open' | 'done' | 'snoozed';

export interface PlannedReminder {
  sourceKey: string;
  type: ReminderType;
  dueDate: ISODate;
  note: string;
}

export interface ReminderPayment {
  id: string;
  slotKey: string | null;
  label?: string;
  status: PaymentStatus;
  expectedDate: ISODate | null;
  receivedDate?: ISODate | null;
}

export interface DealReminderInput {
  dealId: string;
  dealType: DealType;
  dealName: string;
  dates: DealDates;
  termMonths?: number | null;
  payments: ReminderPayment[];
  today: ISODate;
}

export function planDealReminders(d: DealReminderInput): PlannedReminder[] {
  const out: PlannedReminder[] = [];
  const key = (suffix: string) => `deal:${d.dealId}:${suffix}`;

  if (isLeaseDealType(d.dealType)) {
    // Lease-expiration touchpoint: one year before expiration. It follows the
    // expiration date, so a confirmed commencement moves it automatically.
    const exp = leaseExpiration(d.dates, d.termMonths);
    if (exp.date) {
      out.push({
        sourceKey: key('lease_expiration'),
        type: 'lease_expiration_touchpoint',
        dueDate: addYears(exp.date, -1),
        note: `${d.dealName}: lease expires ${exp.date} (${exp.basis}). Reach out about renewal or relocation.`,
      });
    }
    // Post-close touchpoint: 6 months after the final payment, once fully paid.
    const fullyPaid = d.payments.length > 0 && d.payments.every((p) => p.status === 'received');
    const lastReceived = maxDate(d.payments.map((p) => p.receivedDate).filter((x): x is ISODate => !!x));
    if (fullyPaid && lastReceived) {
      out.push({
        sourceKey: key('post_close_6mo'),
        type: 'post_close_touchpoint',
        dueDate: addMonths(lastReceived, 6),
        note: `${d.dealName}: 6-month check-in after the deal was fully paid.`,
      });
    }
  } else if (d.dates.contractEffective) {
    // Under Contract milestones.
    const { ddEnd, closing } = underContractDates(d.dates);
    out.push({
      sourceKey: key('contract_effective'),
      type: 'deal_next_step',
      dueDate: d.dates.contractEffective,
      note: `${d.dealName}: contract effective date.`,
    });
    if (ddEnd) {
      out.push({ sourceKey: key('dd_end'), type: 'deal_next_step', dueDate: ddEnd, note: `${d.dealName}: due diligence ends.` });
    }
    if (closing) {
      out.push({ sourceKey: key('closing'), type: 'deal_next_step', dueDate: closing, note: `${d.dealName}: scheduled closing.` });
    }
  }

  // Chase every overdue payment.
  for (const p of d.payments) {
    if (effectivePaymentStatus(p, d.today) === 'overdue' && p.expectedDate) {
      out.push({
        sourceKey: key(`payment_chase:${p.slotKey ?? p.id}`),
        type: 'payment_chase',
        dueDate: d.today,
        note: `${d.dealName}: payment${p.label ? ` "${p.label}"` : ''} was due ${p.expectedDate} and isn't marked received yet — invoice/chase it.`,
      });
    }
  }
  return out;
}

export interface StoredReminder {
  id: string;
  sourceKey: string;
  status: ReminderStatus;
  dueDate: ISODate;
}

export interface ReminderChanges {
  toInsert: PlannedReminder[];
  toUpdate: { id: string; dueDate: ISODate; note: string }[];
  /** Open auto-reminders whose reason has gone away (e.g. the payment was received). */
  toClose: string[];
}

/** Completed reminders are never re-opened, moved or recreated. */
export function reconcileReminders(existing: StoredReminder[], planned: PlannedReminder[]): ReminderChanges {
  const byKey = new Map(existing.map((r) => [r.sourceKey, r]));
  const plannedKeys = new Set(planned.map((p) => p.sourceKey));
  const changes: ReminderChanges = { toInsert: [], toUpdate: [], toClose: [] };

  for (const p of planned) {
    const current = byKey.get(p.sourceKey);
    if (!current) changes.toInsert.push(p);
    else if (current.status !== 'done' && current.dueDate !== p.dueDate) {
      changes.toUpdate.push({ id: current.id, dueDate: p.dueDate, note: p.note });
    }
  }
  for (const r of existing) {
    if (r.status !== 'done' && !plannedKeys.has(r.sourceKey)) changes.toClose.push(r.id);
  }
  return changes;
}
